import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PasswordInput from "@/components/PasswordInput";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Mail, Lock } from "lucide-react";
import { halamanAwal } from "@/lib/roles";
import { JUDUL_LUPA_SANDI, PESAN_LUPA_SANDI } from "@/lib/akunAdmin";
import AuthShell, {
    AuthIconField, AuthLoadingIndicator, AUTH_LABEL_CLASS, AUTH_TITLE_CLASS, AUTH_PRIMARY_BUTTON_CLASS, AUTH_SECONDARY_BUTTON_CLASS,
} from "@/components/AuthShell";

export default function Login() {
    const { login } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [forgotOpen, setForgotOpen] = useState(false);
    // Ganti tiap submit (sukses/gagal) -> me-remount PasswordInput di bawah, memaksa
    // kolom password balik tersembunyi (lihat PasswordInput.jsx).
    const [pwResetKey, setPwResetKey] = useState(0);
    const navigate = useNavigate();
    const location = useLocation();

    // Pesan singkat setelah auto-logout karena idle (lihat IdleTimeoutManager).
    // Dibaca sekali dari location.state saat mount, lalu state dibersihkan
    // supaya toast tidak muncul lagi kalau user reload/navigasi balik.
    useEffect(() => {
        if (location.state?.idleTimeout) {
            toast.info("Sesi Anda berakhir karena tidak ada aktivitas. Silakan masuk kembali.");
            navigate(location.pathname, { replace: true, state: {} });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Pesan singkat saat interceptor 401 global (lib/api.js -> AuthContext) memaksa
    // keluar krn sesi tidak valid lagi (token expired/dicabut, atau akun dinonaktifkan
    // admin -- lihat AuthContext.jsx). Pola sama persis dgn idleTimeout di atas.
    useEffect(() => {
        if (location.state?.sessionExpired) {
            toast.info("Sesi Anda telah berakhir, silakan login kembali.");
            navigate(location.pathname, { replace: true, state: {} });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        const res = await login(email, password);
        setLoading(false);
        setPwResetKey((k) => k + 1);
        if (!res.ok) return toast.error(res.error);
        if (res.otpRequired) {
            navigate("/verifikasi-otp", { state: { otpToken: res.otpToken, email } });
            return;
        }
        if (res.totpRequired) {
            navigate("/verifikasi-totp", { state: { challengeToken: res.challengeToken, email } });
            return;
        }
        if (res.totpEnrollRequired) {
            navigate("/verifikasi-totp", { state: { enrollToken: res.enrollToken, email } });
            return;
        }
        toast.success("Login berhasil");
        if (res.user.must_change_password) navigate("/ubah-password");
        else navigate(halamanAwal(res.user.role));
    };

    return (
        <AuthShell>
            <div className="mb-7 text-center">
                <h1 className={`text-[28px] sm:text-3xl ${AUTH_TITLE_CLASS}`}>Selamat Datang</h1>
                <p className="mt-1.5 text-sm text-slate-600 sm:text-[15px]">Silakan masuk menggunakan akun Anda.</p>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-5" data-testid="login-form">
                <div className="flex flex-col gap-2">
                    <Label htmlFor="email" className={AUTH_LABEL_CLASS}>Email</Label>
                    <AuthIconField icon={Mail}>
                        <Input
                            id="email"
                            data-testid="login-email-input"
                            type="email"
                            required
                            autoComplete="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="nama@email.com"
                            className="h-[50px] rounded-none border-0 px-3.5 text-[15px] shadow-none focus-visible:ring-0 md:text-[15px]"
                        />
                    </AuthIconField>
                </div>
                <div className="flex flex-col gap-2">
                    <Label htmlFor="password" className={AUTH_LABEL_CLASS}>Kata Sandi</Label>
                    <AuthIconField icon={Lock}>
                        <PasswordInput
                            key={pwResetKey}
                            id="password"
                            data-testid="login-password-input"
                            required
                            autoComplete="current-password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Masukkan kata sandi Anda"
                            className="h-[50px] rounded-none border-0 pl-3.5 text-[15px] shadow-none focus-visible:ring-0 md:text-[15px]"
                        />
                    </AuthIconField>
                </div>

                <Button
                    type="submit"
                    data-testid="login-submit-button"
                    disabled={loading}
                    aria-busy={loading ? "true" : undefined}
                    className={`mt-1 ${AUTH_PRIMARY_BUTTON_CLASS}${loading ? " disabled:opacity-100" : ""}`}
                >
                    {loading ? <AuthLoadingIndicator /> : "MASUK"}
                </Button>
                <Button
                    type="button"
                    variant="secondary"
                    data-testid="login-forgot-password-link"
                    onClick={() => setForgotOpen(true)}
                    className={AUTH_SECONDARY_BUTTON_CLASS}
                >
                    LUPA KATA SANDI
                </Button>
            </form>

            {/* "Lupa Kata Sandi?" hanya informasi sejak Okt 2026 -- tidak ada email dari halaman login. */}
            <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
                <DialogContent>
                    <DialogHeader><DialogTitle>{JUDUL_LUPA_SANDI}</DialogTitle></DialogHeader>
                    <p className="text-sm text-slate-700" data-testid="forgot-password-info">{PESAN_LUPA_SANDI}</p>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => setForgotOpen(false)} data-testid="forgot-password-close">
                            Tutup
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </AuthShell>
    );
}
